"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { ActionCard } from "@/components/ActionCard";
import {
  MONITORING_METRICS,
  MonitoringMetricKey,
  MonitoringTrendChart,
  type MonitoringTrendPoint,
} from "@/components/cards/MonitoringTrendChart";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const METRIC_DOT_COLORS: Record<MonitoringMetricKey, string> = {
  overall: "#3b82f6",
  performance: "#22c55e",
  quality: "#f59e0b",
  trust: "#ef4444",
  dr: "#8b5cf6",
};

export function MonitoringCard() {
  const mockData: MonitoringTrendPoint[] = useMemo(() => {
    const scores: Array<Omit<MonitoringTrendPoint, "date">> = [
      { overall: 40, performance: 45, quality: 41, trust: 33 },
      { overall: 45, performance: 54, quality: 46, trust: 34 },
      { overall: 48, performance: 59, quality: 49, trust: 35 },
      { overall: 50, performance: 62, quality: 52, trust: 36 },
      { overall: 52, performance: 65, quality: 54, trust: 38 },
      { overall: 54, performance: 67, quality: 56, trust: 39 },
      { overall: 56, performance: 69, quality: 58, trust: 40 },
      { overall: 57, performance: 71, quality: 60, trust: 41 },
      { overall: 59, performance: 72, quality: 62, trust: 43 },
      { overall: 61, performance: 74, quality: 64, trust: 44 },
      { overall: 62, performance: 75, quality: 65, trust: 46 },
      { overall: 64, performance: 76, quality: 67, trust: 48 },
      { overall: 65, performance: 77, quality: 68, trust: 49 },
      { overall: 66, performance: 78, quality: 70, trust: 51 },
      { overall: 68, performance: 79, quality: 71, trust: 53 },
      { overall: 69, performance: 79, quality: 72, trust: 56 },
      { overall: 70, performance: 80, quality: 73, trust: 58 },
      { overall: 72, performance: 81, quality: 75, trust: 60 },
      { overall: 73, performance: 82, quality: 76, trust: 62 },
      { overall: 75, performance: 83, quality: 77, trust: 65 },
      { overall: 77, performance: 84, quality: 79, trust: 67 },
      { overall: 78, performance: 86, quality: 80, trust: 69 },
      { overall: 80, performance: 87, quality: 81, trust: 71 },
      { overall: 81, performance: 88, quality: 83, trust: 73 },
      { overall: 82, performance: 89, quality: 84, trust: 74 },
      { overall: 84, performance: 91, quality: 86, trust: 76 },
      { overall: 86, performance: 92, quality: 87, trust: 78 },
      { overall: 87, performance: 93, quality: 89, trust: 80 },
      { overall: 89, performance: 94, quality: 90, trust: 82 },
      { overall: 90, performance: 95, quality: 92, trust: 84 },
      { overall: 92, performance: 96, quality: 93, trust: 86 },
    ];

    const today = new Date();
    return scores.map((entry, idx) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (scores.length - 1 - idx));
      // DR climbs slowly and independently from the on-page scores.
      const dr = 18 + Math.round(idx * 1.4);
      return { date: date.toISOString().slice(0, 10), ...entry, dr };
    });
  }, []);

  const metricOrder: MonitoringMetricKey[] = [
    "overall",
    "performance",
    "quality",
    "trust",
    "dr",
  ];
  const [activeMetric, setActiveMetric] =
    useState<MonitoringMetricKey>("overall");
  const indicatorLabel = MONITORING_METRICS[activeMetric].label;

  const header = (
    <div className="mb-4 w-full aspect-[16/9] rounded-lg border border-border/60 bg-muted/10 p-3 flex flex-col">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Image
            src="/logo.svg"
            alt=""
            aria-hidden
            width={16}
            height={16}
            className="h-4 w-4"
          />
          <span className="text-[11px] text-muted-foreground/80">
            yourwebsitescore.com
          </span>
        </div>
        {/* Metric selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-2 py-1 text-[11px] font-medium text-muted-foreground/90 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all hover:border-border hover:bg-background/90 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 cursor-pointer"
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                aria-hidden
                style={{ backgroundColor: METRIC_DOT_COLORS[activeMetric] }}
              />
              <span>{indicatorLabel}</span>
              <ChevronDown
                className="h-3.5 w-3.5 text-muted-foreground/70"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {metricOrder.map((metricKey) => {
              const metricConfig = MONITORING_METRICS[metricKey];
              const isActive = metricKey === activeMetric;
              return (
                <DropdownMenuItem
                  key={metricKey}
                  onSelect={() => setActiveMetric(metricKey)}
                  className="flex items-center gap-2 text-sm"
                  data-state={isActive ? "checked" : undefined}
                >
                  <span
                    className="inline-flex h-2 w-2 rounded-full"
                    aria-hidden
                    style={{ backgroundColor: METRIC_DOT_COLORS[metricKey] }}
                  />
                  <span
                    className={
                      isActive ? "text-foreground" : "text-muted-foreground"
                    }
                  >
                    {metricConfig.label}
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-2 flex-1">
        <MonitoringTrendChart data={mockData} metric={activeMetric} />
      </div>
    </div>
  );

  return (
    <ActionCard
      ariaLabel="Monitoring"
      title="Monitoring"
      subtitle="We monitor your domain daily and notify you immediately when critical changes occur"
      header={header}
    />
  );
}
